import React from 'react'
import { Link } from 'react-router-dom'
import './Services.css'

const services = [
  {
    id: 1,
    tag: 'CI/CD',
    title: 'Pipeline Design & Implementation',
    desc: 'We design end-to-end pipelines tailored to your repo structure and release cadence. From simple GitHub Actions workflows to enterprise-grade multi-stage Jenkins or ArgoCD rollouts.',
    features: [
      'Automated build, test & security scan stages',
      'Multi-environment promotion (dev → staging → prod)',
      'Canary and blue/green deployment strategies',
      'Rollback automation and pipeline observability',
    ],
    color: '#6c63ff',
    icon: '🔄',
  },
  {
    id: 2,
    tag: 'Kubernetes',
    title: 'Cluster Setup & Operations',
    desc: 'Production-ready Kubernetes environments configured for resilience, security, and cost efficiency. We handle the day-0 setup through day-2 operations.',
    features: [
      'Cluster provisioning on EKS, AKS, or GKE',
      'RBAC, Network Policies, and OPA/Gatekeeper',
      'Horizontal & Vertical Pod Autoscaling',
      'Managed node groups, spot instances & cost tuning',
    ],
    color: '#00c8ff',
    icon: '☸️',
  },
  {
    id: 3,
    tag: 'IaC',
    title: 'Infrastructure as Code',
    desc: 'Every piece of cloud infrastructure, defined in code. We author, review, and maintain Terraform and Pulumi modules that are tested, versioned, and repeatable.',
    features: [
      'Terraform module libraries for AWS, Azure & GCP',
      'State management with remote backends & locking',
      'Policy-as-code with Sentinel or OPA',
      'Automated drift detection and remediation',
    ],
    color: '#00e676',
    icon: '🏗️',
  },
  {
    id: 4,
    tag: 'Observability',
    title: 'Monitoring & Alerting',
    desc: 'You cannot improve what you cannot measure. We deploy and configure the full observability stack so your team has the signal they need when things go wrong — or before they do.',
    features: [
      'Prometheus + Grafana dashboards',
      'Distributed tracing with OpenTelemetry',
      'Log aggregation with Loki or ELK',
      'PagerDuty / OpsGenie alert routing',
    ],
    color: '#ff9800',
    icon: '📊',
  },
  {
    id: 5,
    tag: 'Cloud Migration',
    title: 'Lift, Shift & Modernise',
    desc: 'Moving workloads to the cloud or between cloud providers? We plan and execute migrations with minimal disruption, containerising legacy apps where appropriate.',
    features: [
      'Application containerisation assessments',
      'Database migration planning and execution',
      'Network architecture and VPC design',
      'Cost optimisation post-migration',
    ],
    color: '#ff5c8d',
    icon: '🚀',
  },
  {
    id: 6,
    tag: 'DevSecOps',
    title: 'Security Integration',
    desc: 'Shift security left. We embed scanning, policy enforcement, and compliance checks directly into your pipelines so vulnerabilities are caught before they reach production.',
    features: [
      'Container image scanning (Trivy, Snyk)',
      'SAST/DAST in pipeline stages',
      'Secrets management with Vault or AWS Secrets Manager',
      'CIS benchmark compliance reporting',
    ],
    color: '#f44336',
    icon: '🔒',
  },
]

export default function Services() {
  return (
    <main style={{ paddingTop: '80px' }}>
      {/* ── Header ───────────────────────────────── */}
      <section className="section">
        <div className="container services-header">
          <span className="badge">Services</span>
          <h1>Engineering-Led <span className="text-accent">IT Delivery</span></h1>
          <p>GT Expression offers a focused set of cloud and DevOps delivery services — each one designed to remove toil, reduce risk, and accelerate your team's ability to ship software safely.</p>
        </div>
      </section>

      {/* ── Service cards ─────────────────────────── */}
      <section className="section section--alt">
        <div className="container">
          <div className="services-grid">
            {services.map(s => (
              <div key={s.id} className="service-card" style={{ '--svc-color': s.color }}>
                <div className="service-card__header">
                  <span className="service-card__icon">{s.icon}</span>
                  <span className="service-card__tag" style={{ color: s.color, borderColor: `${s.color}40`, background: `${s.color}12` }}>
                    {s.tag}
                  </span>
                </div>
                <h3 className="service-card__title">{s.title}</h3>
                <p className="service-card__desc">{s.desc}</p>
                <ul className="service-card__features">
                  {s.features.map(f => (
                    <li key={f}>
                      <span style={{ color: s.color }}>▸</span> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────── */}
      <section className="section">
        <div className="container services-cta">
          <h2>Not sure where to start?</h2>
          <p>Every engagement begins with a no-obligation architecture review. We'll map your current state and identify the highest-leverage improvements.</p>
          <Link to="/platforms" className="btn btn--primary">Explore Platforms →</Link>
        </div>
      </section>
    </main>
  )
}
